import { useCallback, useEffect } from 'react';

import {
  ApiResult,
  useApiDelete,
  useApiGet,
  useApiPatch,
  useApiPost
} from '../../common/api/Api.hooks';
import { useDownloadBlob } from '../../common/blob.hook';
import { useIdSearchableFetch } from '../../common/entityId.hooks';
import { PaginatedList } from '../../common/PaginatedList.interface';
import { ChangeOrder } from '../changeOrder/ChangeOrder.interface';
import { Group } from '../groups/Group.interface';
import { parseTaskStatus } from '../tasks/task.helper';
import { Task } from '../tasks/Task.interface';
import { WorkflowEntity } from '../workflow/Workflow.interface';
import {
  TrainingSendReminderResponse,
  Training,
  TrainingGroupDto,
  TrainingStatusLog,
  TrainingTransition,
  TrainingUpdateDto,
  TrainingVersion
} from './Training.interface';

const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function getStatusLabel(status: string) {
  switch (status) {
    case 'to-do':
      return 'To-Do';
    case 'draft':
      return 'Draft';
    case 'in-review':
      return 'In Review';
    case 'approved':
      return 'Approved';
  }
}

export function useCreateTraining() {
  const { data, isLoading, error, post } = useApiPost<Training>('trainings');
  return { training: data, isLoading, error, post };
}

export function useUpdateTraining(id: number) {
  const { data, isLoading, error, patch } = useApiPatch<Training>(
    `trainings/${id}`
  );
  const updateTraining = useCallback(
    (params: Partial<TrainingUpdateDto>) => patch(params),
    [patch]
  );
  return { training: data, isLoading, error, patch: updateTraining };
}

export function useRevertTraining(id: number) {
  const { data, isLoading, error, post } = useApiPost<Training>(
    `trainings/${id}/revert`
  );
  return { training: data, isLoading, error, revert: post };
}

export function useAddTrainingGroup(id: number) {
  const { data, isLoading, error, post } = useApiPost<Group>(
    `trainings/${id}/groups`
  );
  return { group: data, isLoading, error, addTrainingGroup: post };
}

export function useFindTrainings(shouldFetchOnMount = false) {
  const { data, isLoading, error, get } =
    useApiGet<PaginatedList<Training>>('trainings');
  const findTrainings = useIdSearchableFetch(get, {
    from: 'filter',
    entity: WorkflowEntity.Training
  });
  useEffect(() => {
    if (shouldFetchOnMount) {
      findTrainings();
    }
  }, [findTrainings, shouldFetchOnMount]);
  return { trainingList: data, isLoading, error, get: findTrainings };
}

export function useFindTraining(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<Training>(
    `trainings/${id}`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, id, shouldFetchOnMount]);
  return { training: data, isLoading, error, get };
}

export function useFindTrainingByTaskId(
  taskId: number,
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<Training>(
    'trainings/task/' + taskId
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { training: data, isLoading, error, get };
}

export function useFindTrainingVersion(
  trainingId: number,
  trainingVersionId: number | 'latest',
  shouldFetchOnMount = false
) {
  const { data, isLoading, error, get } = useApiGet<TrainingVersion>(
    `trainings/${trainingId}/version/${trainingVersionId}`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, trainingVersionId, shouldFetchOnMount]);
  return {
    trainingVersion: data,
    isLoading,
    findError: error,
    findTrainingVersion: get
  };
}

export function useFindTrainingChangeOrders(trainingId: number) {
  const { data, isLoading, error, get } = useApiGet<ChangeOrder[]>(
    `trainings/${trainingId}/change-orders`
  );
  return { changeOrders: data, isLoading, error, findChangeOrders: get };
}

export function useGetStatusLogs(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<
    PaginatedList<TrainingStatusLog>
  >(`trainings/${id}/status-log`);
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { trainingStatusLogs: data, isLoading, error, get };
}

export function useFindLatestTrainingVersions() {
  const { data, get, ...result } =
    useApiGet<TrainingVersion[]>('trainings/versions');
  return { ...result, trainingVersions: data, findTrainingVersions: get };
}

export function useGetTrainingVersions(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<TrainingVersion[]>(
    `trainings/${id}/versions`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { trainingVersions: data, isLoading, error, get };
}

export function useGetTrainingGroups(id: number, shouldFetchOnMount = false) {
  const { data, isLoading, error, get } = useApiGet<TrainingGroupDto[]>(
    `trainings/${id}/groups`
  );
  useEffect(() => {
    if (shouldFetchOnMount) {
      get();
    }
  }, [get, shouldFetchOnMount]);
  return { trainingGroups: data, isLoading, error, get };
}

export function useDeleteTrainingGroups(id: number) {
  const { data, isLoading, error, del } = useApiDelete<TrainingGroupDto>(
    `trainings/${id}/groups`
  );
  return {
    deletedGroups: data,
    isDeleting: isLoading,
    error,
    del
  };
}

export function getTrainingTransitionType(
  previousTask: Task,
  updatedTask: Task
): TrainingTransition {
  const previousStatus: string = parseTaskStatus(previousTask.status);
  const updatedStatus: string = parseTaskStatus(updatedTask.status);
  // assumes "Review" and "Approved" statuses exist
  const matchReview: string = 'Review'; // In Review
  const matchApproved: string = 'Approved';
  const previousApproved: boolean = previousStatus.includes(matchApproved);
  const previousInReview: boolean = previousStatus.includes(matchReview);
  const updatedApproved: boolean = updatedStatus.includes(matchApproved);

  if (updatedApproved && !previousApproved) {
    // Approve if transitioning from Review
    return previousInReview
      ? TrainingTransition.Approve
      : TrainingTransition.Revert;
  }

  return TrainingTransition.Transition;
}

export function useExportTrainee(
  id: number
): [
  ApiResult<any>,
  (extension: 'docx' | 'pdf') => void,
  (fileName: string) => void
] {
  const { get, ...result } = useApiGet<any>(`users/trainees/${id}/export`);
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (extension: 'docx' | 'pdf') => {
      // remove old document
      setBlob(undefined);
      get({ extension, timeZone });
    },
    [get]
  );

  return [result, getDocument, downloadDocument];
}

export function useExportTrainingPlan(
  id: number
): [
  ApiResult<any>,
  (option: 'all' | 'current') => void,
  (fileName: string) => void
] {
  const { get, ...result } = useApiGet<Training>(`trainings/${id}/export`);
  const { response } = result;
  const { setBlob, downloadDocument } = useDownloadBlob(response);

  const getDocument = useCallback(
    (option: 'all' | 'current') => {
      // remove old document
      setBlob(undefined);
      get({ option, timeZone });
    },
    [get]
  );

  return [result, getDocument, downloadDocument];
}

export function useSendTrainingReminder(id: number) {
  const { data, isLoading, error, post } =
    useApiPost<TrainingSendReminderResponse>(`trainings/${id}/send-reminder`);

  return { data, isLoading, error, sendReminder: post };
}
